import React, { useRef, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { parse } from 'query-string';
import { flatten, isNil, update, without } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';

import { EmptyState, MobileTable, Paper } from '@dev/ee-components';
import { CampaignStatus, CompressionFormat, ExportFileFormats } from '@dev/ee-api/ee-api-models';

import {
    PickCampaigns,
    UnpickAllCampaigns,
    GetCampaigns,
    getCampaignsStore,
    ExportCampaigns,
    DeleteCampaigns,
} from '@/store/campaigns/actions';
import { getAccountAccessLevel } from '@/store/account/actions';
import { getConfigStore } from '@/store/config/actions';
import { ShowDialog } from '@/store/dialog/actions';
import ExportModal from '@/components/ExportModal';
import { GetCampaignsPayload } from '@/store/campaigns/types';
import { useCustomIsMobile } from '@/services/hooks/useCustomIsMobile';
import { ExportCampaignsParams } from '@/services/ElasticApi';
import { MobileTableContainer } from '@/components/Campaigns/components/StyledComponents';
import { DesktopListTable } from '@/components/Campaigns/components/DesktopListTable';
import { CampaignStatusNone, CampaignStatusExtended, screenCampaignTypes } from '../../config';
import {
    goToCampaign,
    getTableHeaders,
    getCampaignsListEmptyRows,
    getCampaignsListRows,
    getMobileTableHeaders,
    getMobileTableRows,
    getMobileDropdownTableOptions,
    mobileDropdownDataFilter,
    rowDropdownOptionsHandler,
} from '../../helpers';

const CampaignsListResults = (): JSX.Element => {
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useDispatch();
    const accountAl = useSelector(getAccountAccessLevel);
    const campaigns = useSelector(getCampaignsStore);
    const config = useSelector(getConfigStore);
    const exportOptions = useRef<ExportCampaignsParams>({
        campaignNames: [],
        fileName: null,
        compressionFormat: CompressionFormat.Zip,
        fileFormat: ExportFileFormats.Csv,
    });
    const isMobile = useCustomIsMobile();
    const fileNameCampaignsRef = useRef<HTMLInputElement>();
    const query = parse(location.search);

    const onSelectedCampaign = (event: SyntheticEvent<HTMLInputElement>, pickedCampaigns: string[]): void => {
        if (!isNil(event)) {
            event.stopPropagation();
            event.preventDefault();
        }
        dispatch(PickCampaigns(pickedCampaigns));
    };

    const openExportCampaignsModal = (): void => {
        dispatch(
            ShowDialog({
                title: t('exportcampaigns'),
                body: (
                    <ExportModal
                        onCompressionChange={changeExportCompression}
                        onFileFormatChange={changeExportFileFormat}
                        inputRef={(element) => (fileNameCampaignsRef.current = element)}
                    />
                ),
                confirmLabel: t('export'),
                cancelLabel: t('cancel'),
                onClickConfirm: exportCampaigns,
            })
        );
    };
    const changeExportCompression = (isEnabled: boolean): void => {
        update(exportOptions.current, 'compressionFormat', () =>
            isEnabled ? CompressionFormat.Zip : CompressionFormat.None
        );
    };
    const changeExportFileFormat = (data: { value: ExportFileFormats; label: string }): void => {
        update(exportOptions.current, 'fileFormat', () => data.value);
    };

    const exportCampaigns = (): void => {
        const { compressionFormat, fileFormat } = exportOptions?.current || {};

        const fileName = fileNameCampaignsRef.current.value || 'export';
        const exportParams = {
            campaignNames: campaigns.checkedCampaigns,
            fileFormat,
            compressionFormat,
            fileName,
        };
        dispatch(ExportCampaigns(exportParams));
    };

    const handleDeleteClick = (): void => {
        dispatch(DeleteCampaigns(campaigns.checkedCampaigns, screenCampaignTypes));
    };
    const onFilterClick = (event: SyntheticEvent<HTMLInputElement>, checkedStatus: CampaignStatusExtended[]): void => {
        const { perPage: limit } = campaigns.pagination;
        const statuses = without(checkedStatus, CampaignStatusNone.Empty) as CampaignStatus[];
        const params: GetCampaignsPayload = {
            status: statuses,
            offset: 0,
            limit,
            type: screenCampaignTypes,
            search: campaigns.pagination.search,
            from: campaigns.pagination?.from,
            to: campaigns.pagination?.to,
        };
        dispatch(UnpickAllCampaigns());
        dispatch(GetCampaigns(params));
    };
    const handleMobileOpenItem = (ev, singleCampaign): void => {
        goToCampaign(dispatch, config, singleCampaign.returnOnItemClick);
    };
    const tableHeaders = getTableHeaders({
        campaigns,
        selectedStatusFilters: query?.status ? flatten([query?.status]) : [''],
        onFilterClick,
        accountAccessLevels: accountAl,
        onExportCampaignsClick: openExportCampaignsModal,
        onDeleteCampaignsClick: handleDeleteClick,
    });

    if (isMobile) {
        if (!campaigns?.loading && !campaigns?.statusCount?.total) {
            return (
                <Paper>
                    <EmptyState text={t('emptystate')} />
                </Paper>
            );
        }
        const rows = campaigns?.loading ? getCampaignsListEmptyRows(isMobile) : getMobileTableRows(campaigns);
        return (
            <MobileTableContainer>
                <MobileTable
                    headers={getMobileTableHeaders()}
                    rows={rows}
                    dropdownData={getMobileDropdownTableOptions()}
                    dropdownDataFilter={mobileDropdownDataFilter}
                    buttonText={t('viewreports')}
                    onClickButton={handleMobileOpenItem}
                    onItemClick={(ev, option: { label: string; value: string }, singleCampaign): void => {
                        rowDropdownOptionsHandler(dispatch, config, option, singleCampaign);
                    }}
                />
            </MobileTableContainer>
        );
    }
    const rows = campaigns?.loading
        ? getCampaignsListEmptyRows(isMobile, campaigns?.pagination?.perPage)
        : getCampaignsListRows(dispatch, config, campaigns);

    return <DesktopListTable headers={tableHeaders} rows={rows} onSelectRow={onSelectedCampaign} />;
};

export default CampaignsListResults;
